import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');
      
      if (token && savedUser && savedUser !== 'null' && savedUser !== 'undefined') {
        try {
          // First restore user from localStorage for immediate UI update
          const parsedUser = JSON.parse(savedUser);
          setUser(parsedUser);
          
          // Skip server validation on initial load to prevent logout issues
          // The token will be validated on the first API call anyway
          console.log('User restored from localStorage, skipping server validation');
        } catch (error) {
          // Handle JSON parsing errors or other errors
          if (error instanceof SyntaxError) {
            // Invalid JSON in localStorage, clear it
            localStorage.removeItem('user');
            console.error('Invalid user data in localStorage, cleared');
          }
          console.error('Error restoring user from localStorage:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []); // Empty dependency array - only run once on mount

  const login = async (email, password, studentId = null, admissionNumber = null) => {
    try {
      // Support both email+password and studentId+admissionNumber
      const loginData = studentId && admissionNumber 
        ? { studentId, admissionNumber }
        : { email, password };
      
      const response = await authAPI.login(loginData);
      setUser(response.data.data.user);
      localStorage.setItem('token', response.data.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.data.user));
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  };

  // Add a method to clear auth state (for API interceptor)
  const clearAuth = () => {
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  const hasRole = (requiredRoles) => {
    if (!user) return false;
    if (Array.isArray(requiredRoles)) {
      return requiredRoles.includes(user.role);
    }
    return user.role === requiredRoles;
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    
    // Define role-based permissions
    const permissions = {
      student: ['view_assignments', 'submit_assignments', 'view_grades', 'view_attendance', 'view_timetable'],
      teacher: ['create_assignments', 'grade_assignments', 'mark_attendance', 'view_students', 'create_exams', 'grade_exams'],
      parent: ['view_children', 'view_children_grades', 'view_children_attendance', 'send_messages'],
      staff: ['manage_users', 'view_reports', 'create_announcements', 'manage_events'],
      admin: ['all']
    };

    const userPermissions = permissions[user.role] || [];
    return userPermissions.includes(permission) || userPermissions.includes('all');
  };

  // Method to update user data (e.g., after profile picture upload)
  const updateUser = (updatedUserData) => {
    if (!user) return;
    const updatedUser = { ...user, ...updatedUserData };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const value = {
    user,
    loading,
    login,
    logout,
    clearAuth,
    hasRole,
    hasPermission,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
